import * as React from 'react';

import dynamic from 'next/dynamic';

import TuneIcon from '@mui/icons-material/Tune';
import {
  Button,
  CircularProgress,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { ListingRequest } from '@kamernet/core/ApiClient/Kamernet';
import { reportError } from '@kamernet/core/Errors';
import { useIntl } from '@kamernet/core/Intl';

import { useFilterSelectors } from '@kamernet/modules/Listing/context/hooks/useFilterSelectors';
import { useListingStateDispatcher } from '@kamernet/modules/Listing/hooks/useListingStateDispatcher';

import styles from './FilterForm.module.css';

const DynamicForm = dynamic(
  () =>
    import('./components/DynamicFilterForm/DynamicFilterForm').then(
      ({ DynamicFilterForm }) => DynamicFilterForm,
    ),
  {
    ssr: false,
    loading: () => <CircularProgress className={styles.spinner} />,
  },
);

type AnchorPosition = 'bottom' | 'right';

type FilterViewPosition = {
  filterViewPosition: AnchorPosition;
};

const FilterFormInternal = ({ filterViewPosition }: FilterViewPosition) => {
  const { listingStateDispatcher } = useListingStateDispatcher();
  const { getFiltersCount } = useFilterSelectors();
  const [showFiterView, setShowFiterView] = React.useState(false);
  const { T } = useIntl();
  const theme = useTheme();
  const isMediaSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const toggleSideFilter = React.useCallback(
    () => async (event: React.KeyboardEvent | React.MouseEvent) => {
      if (
        event.type === 'keydown' &&
        ((event as React.KeyboardEvent).key === 'Tab' ||
          (event as React.KeyboardEvent).key === 'Shift')
      ) {
        return;
      }
      setShowFiterView(!showFiterView);
    },
    [showFiterView],
  );

  const onFilterFormSubmit = React.useCallback(
    (values: ListingRequest) => {
      try {
        setShowFiterView(prev => !prev);
        listingStateDispatcher(values);
      } catch (error) {
        reportError(error as Error, {
          extra: {
            context: 'Update listings from sidebar failed - Listing search',
            metaData: { error },
          },
        });
      }
    },
    [listingStateDispatcher],
  );

  return (
    <React.Fragment>
      <Button
        variant={!isMediaSmDown ? 'contained' : 'outlined'}
        color="secondary"
        className={styles.filterButton}
        size={!isMediaSmDown ? 'medium' : 'small'}
        startIcon={<TuneIcon />}
        onClick={toggleSideFilter()}
      >
        {getFiltersCount() > 0
          ? T('search_page.filters.filter_amount', getFiltersCount())
          : T('search_page.filters.filter_amount', '0')}
      </Button>
      <Drawer
        classes={{
          paperAnchorBottom: styles.bottomDrawer,
          paperAnchorRight: styles.rightDrawer,
        }}
        anchor={filterViewPosition}
        open={showFiterView}
        onClose={toggleSideFilter()}
      >
        <DynamicForm
          toggleSideFilter={toggleSideFilter}
          onFilterFormSubmit={onFilterFormSubmit}
        />
      </Drawer>
    </React.Fragment>
  );
};
export const FilterForm = React.memo(FilterFormInternal);
